<template>
	<v-list-item class="px-3" :class="{
						'error': position.price === null,
						'lighten-3': position.price === null,
					}">
		<v-list-item-content>
			<v-list-item-title class="mb-2 item-pos" style="cursor: pointer" @click="showItem(position)">
				{{ itemTitle(position.item) }}
			</v-list-item-title>
			<div class="d-flex flex-row align-center gap-05 mb-2 flex-wrap">
				<ItemChipNumber x-small :item="position.item" />
				<ItemChipAltNumber v-if="position.item.alternativeItemNumber !== null" x-small :item="position.item" />
			</div>
			<v-list-item-subtitle class="d-flex flex-row justify-space-between align-center">
				<div class="d-flex flex-row align-center">
					<ItemQuantitySelection :value="position.quantity" :default-value="stepAmount(position.item)" :unit="itemSalesUnitOfMeasure(position)"
					                       x-small class="mr-3" @input="updateQuantity(position, $event)"
					                       :step="stepAmount(position.item)" debounce is-cart-selector/>
					<AsiBtn :icon="icons.delete" x-small @click.stop="removePosition(position)"/>
				</div>
				<span v-if="position.price !== null">
									{{
						$n((finalAmount(position.price) * position.quantity), {
							key: 'currencyDisplay',
							currency: position.price.currency.currencyCode
						})
					}}
								</span>
				<span v-else> {{ $t('cart.terms.itemUnavailable') }} </span>
			</v-list-item-subtitle>
			<v-list-item-subtitle v-if="showCustomerReferences">
				<CartPositionCustomerReferenceInput :position="position" class="mt-2"/>
			</v-list-item-subtitle>
		</v-list-item-content>
	</v-list-item>
</template>

<script lang="ts">
	import {Vue, Component, Prop} from 'vue-property-decorator';
	import ItemChipAltNumber from "@/components/item/ItemChipAltNumber.vue";
	import CartPositionCustomerReferenceInput from "@/components/cart-position/CartPositionCustomerReferenceInput.vue";
	import AsiBtn from "@/components/common/AsiBtn.vue";
	import ItemChipNumber from "@/components/item/ItemChipNumber.vue";
	import ItemQuantitySelection from "@/components/item/shop/ItemQuantitySelection.vue";
	import {ICartPositionShopSimple} from "@/models/cart-position/CartPositionShopModels";
	import {IPrice} from "@/models/price/PriceModels";
	import TotalHelper from "@/helpers/TotalHelper";
	import Snackbar from "@/helpers/Snackbar";
	import {UnitOfMeasure} from "@/helpers/constants";
	import Icon from "@/plugins/icons";
	import {IItemShopSimple} from "@/models/item/ItemShopModels";
	import TranslatedValueHelper from "@/models/translated-value/TranslatedValueHelper";
	import ItemHelper from "@/models/item/ItemHelper";
	import {TranslateResult} from "vue-i18n";

	@Component({
		components: {
			ItemQuantitySelection,
			ItemChipNumber,
			AsiBtn,
			CartPositionCustomerReferenceInput,
			ItemChipAltNumber
		}
	})
	export default class CartPosition extends Vue {

		@Prop({required: true})
		public position!: ICartPositionShopSimple;

		@Prop({type: Boolean, default: false})
		public showCustomerReferences!: boolean;

		private icons = Icon;

		private finalAmount(price: IPrice): number {
			return price.amount + (this.amountsInclVat ? TotalHelper.vatAmount(price) : 0);
		}

		private stepAmount(item: IItemShopSimple): number {
			if (item.isSalesUnitFixed) {
				return item.salesUnitQuantity;
			}
			return item.salesUnitOfMeasure === UnitOfMeasure.unitMeter ? 0.5 : 1;
		}

		private get amountsInclVat(): boolean {
			return this.$store.state.ui.amountsInclVat;
		}

		private removePosition(position: ICartPositionShopSimple): void {
			this.$store.dispatch('cart/removePosition', position.id).catch(() => Snackbar.deleteError());
		}

		private updateQuantity(position: ICartPositionShopSimple, quantity: number): void {
			if (quantity === position.quantity) return;
			this.$store.dispatch('cart/updatePositionQuantity', {
				positionId: position.id,
				quantity: quantity,
			}).catch(() => Snackbar.updateError());
		}

		private itemSalesUnitOfMeasure(position: ICartPositionShopSimple): UnitOfMeasure | null
		{
			return position.item.salesUnitOfMeasure;
		}

		private itemTitle(item: IItemShopSimple): string | TranslateResult | null {
			return TranslatedValueHelper.get(item.name, this.$i18n.locale);
		}

		private showItem(position: ICartPositionShopSimple): void {
			if (this.$router.resolve(ItemHelper.detailRoute(position.item.itemNumber)).route.path === this.$route.path) return;
			this.$router.push(ItemHelper.detailRoute(position.item.itemNumber));
		}

	}
</script>

<style scoped lang="scss">
	.gap-05 {
		gap: 0.5rem;
	}
</style>
